import { observable, computed, action } from 'mobx'
import { computedFn } from 'mobx-utils'
import { Api } from 'utilities'
import {
  RootStore, GENERAL_STORE, EDITOR_GROUPS_STORE,
  EDITOR_LOCATIONS_STORE, ALL_LOCATIONS, UNLOCATED_SHOTS,
  UNGROUPED_SHOTS, EDITOR_STORE
} from 'store'
import { Shot, ShotFromGroup } from './types'

export const EDITOR_SHOTS_STORE = 'EDITOR_SHOTS_STORE'

export class EditorShotsStore {
  @observable shots: { [key: string]: Shot } = {};
  @observable selectedShotIds: number[] = [];
  @observable lastSelectedShotId: number | null = null;
  @observable selectedShotIdsForLocationChange: number[] = [];

  rootStore: RootStore;

  constructor (rootStore: RootStore) {
    this.rootStore = rootStore
  }

  @computed get sortedShotIds () {
    return Object.keys(this.shots)
      .sort((a, b) => this.shots[a].startFrame - this.shots[b].startFrame)
  }

  @computed get sortedShotsFilteredBySelectedLocation () {
    const filterBySelectedLocation = shot => {
      const nonSpecificLocations = [null, ALL_LOCATIONS, UNLOCATED_SHOTS]
      const { selectedLocation } = this.rootStore[EDITOR_LOCATIONS_STORE]
      const isActualLocationSelected = !nonSpecificLocations.includes(selectedLocation)
      // console.log('isActualLocationSelected', isActualLocationSelected);
      const doesSelectedLocationMismatch = isActualLocationSelected &&
        shot.location !== selectedLocation
      if (doesSelectedLocationMismatch) return false
      if (selectedLocation === UNLOCATED_SHOTS && shot.location) return false

      return true
    }
    return this.sortedShotIds
      .map(this.getShotById)
      .filter(filterBySelectedLocation)
  }

  @computed get sortedSelectedShots () {
    return this.sortedShotIds
      .filter(id => this.selectedShotIds.includes(Number(id)))
      .map(this.getShotById)
  }

  @computed get commonRelatedProjectsOfSelectedShots () {
    let commonRelatedProjects = []

    this.sortedSelectedShots.forEach(({ relatedProjects }, index) => {
      if (index === 0) {
        commonRelatedProjects = [...relatedProjects]
        return
      }

      commonRelatedProjects = commonRelatedProjects
        .filter(p => relatedProjects.some(relP => relP.id === p.id))
    })

    return commonRelatedProjects
  }

  @computed get firstSelectedShot () {
    if (!this.selectedShotIds.length) return null

    return this.shots[this.selectedShotIds[0]]
  }

  @computed get selectedShotsForLocationChangeNumber () {
    return this.selectedShotIdsForLocationChange.length
  }

  @observable isShotSelectedForLocationChange = computedFn(shotId => {
    return this.selectedShotIdsForLocationChange.includes(shotId)
  });

  @observable isShotSelected = computedFn(shot => {
    const { id, name: groupName } = shot
    return this.rootStore[EDITOR_GROUPS_STORE].isGroupSelected(groupName) ||
      this.selectedShotIds.includes(id)
  });

  @observable shotsPerGroup = computedFn(groupName => {
    const { shotIds } = this.rootStore[EDITOR_GROUPS_STORE].getGroupByName(groupName)
    const totalShotsInGroup = shotIds.length
    return shotIds
      .map(this.getShotById)
      .sort((a, b) => a.startFrame - b.startFrame)
      .map((shot, index) => ({
        ...shot,
        shotNumberInGroup: index + 1,
        totalShotsInGroup
      }))
  });

  @computed get shotsOfSelectedGroups () {
    const {
      [EDITOR_GROUPS_STORE]: {
        selectedGroups, getGroupByName
      }
    } = this.rootStore

    const shots: ShotFromGroup[] = []

    selectedGroups
      .forEach(groupName => {
        const { shotIds } = getGroupByName(groupName)
        const totalShotsInGroup = shotIds.length
        shotIds
          .slice()
          .map(this.getShotById)
          .sort((a, b) => a.startFrame - b.startFrame)
          .forEach((shot, index) => {
            shots.push({
              ...shot,
              shotNumberInGroup: index + 1,
              totalShotsInGroup
            })
          })
      })
    return shots.sort((a, b) => a.startFrame - b.startFrame)
  }

  getShotById = id => {
    return { ...this.shots[id] }
  }

  @action.bound clearEditorShotsState () {
    this.setShots()
    this.setSelectedShotIds()
    this.setLastSelectedShotId()
    this.setSelectedShotIdsForLocationChange()
  }

  @action.bound setSelectedShotIdsForLocationChange (list = []) {
    this.selectedShotIdsForLocationChange = list
  }

  @action.bound handleInitialShots (shotGroups) {
    let shotsArr = []
    Object.keys(shotGroups)
      .forEach(groupName => {
        shotsArr = shotsArr.concat(shotGroups[groupName])
      })

    const shots = {}
    const groups = {}
    const locations = {}

    shotsArr.forEach(shot => {
      shots[shot.id] = shot

      const groupName = shot.name || UNGROUPED_SHOTS
      if (!groups[groupName]) {
        groups[groupName] = {
          name: groupName,
          shotIds: [shot.id],
          previewFrame: shot.startFrame
        }
      } else {
        groups[groupName].shotIds.push(shot.id)
        groups[groupName].previewFrame =
          Math.min(groups[groupName].previewFrame, shot.startFrame)
      }

      const location = shot.location || UNLOCATED_SHOTS
      if (!locations[location]) {
        locations[location] = {
          name: location,
          shotIds: [shot.id],
          previewFrame: shot.startFrame
        }
      } else {
        locations[location].shotIds.push(shot.id)
        locations[location].previewFrame =
          Math.min(locations[location].previewFrame, shot.startFrame)
      }
    })

    this.setShots(shots)

    const {
      [EDITOR_GROUPS_STORE]: { setGroups },
      [EDITOR_LOCATIONS_STORE]: { setLocations },
      [GENERAL_STORE]: { setLoadingOff }
    } = this.rootStore
    setGroups(groups)
    setLocations(locations)
    setLoadingOff()
  }

  @action.bound setShots (catalog = {}) {
    this.shots = catalog
  }

  @action.bound selectShotToChangeLocation (shotId) {
    this.selectedShotIdsForLocationChange.push(shotId)
  }

  @action.bound deselectShotToChangeLocation (shotId) {
    this.selectedShotIdsForLocationChange = this.selectedShotIdsForLocationChange
      .filter(id => id !== shotId)
  }

  @action.bound switchSelectionOfShotForLocationChange (shotId) {
    this.isShotSelectedForLocationChange(shotId)
      ? this.deselectShotToChangeLocation(shotId)
      : this.selectShotToChangeLocation(shotId)
  }

  @action.bound moveSelectedShotsToLocation (location) {
    const shotsToUpdate = this.selectedShotIdsForLocationChange
      .map(id => ({ ...this.shots[id], location }))
    this.updateShots(shotsToUpdate)
    this.selectedShotIdsForLocationChange = []
  }

  @action.bound onVovWizardShotClick (e, index) {
    const shot = this.sortedShotsFilteredBySelectedLocation[index]

    if (e.shiftKey && this.lastSelectedShotId !== null) {
      const lastSelectedShotIndex = this.sortedShotsFilteredBySelectedLocation
        .findIndex(s => s.id === this.lastSelectedShotId)
      const minIndex = Math.min(index, lastSelectedShotIndex)
      const maxIndex = Math.max(index, lastSelectedShotIndex)
      const selectedShotIds = []
      for (let i = minIndex; i <= maxIndex; i++) {
        selectedShotIds.push(this.sortedShotsFilteredBySelectedLocation[i].id)
      }
      this.setSelectedShotIds(selectedShotIds)
      return
    }

    this.switchShotSelection(shot.id, !e.ctrlKey)
  }

  @action.bound setSelectedShotIds (list = []) {
    this.selectedShotIds = list
  }

  @action.bound setLastSelectedShotId (id = null) {
    this.lastSelectedShotId = id
  }

  @action.bound switchShotSelection (id, singleSelection = false) {
    const index = this.selectedShotIds.findIndex(shotId => shotId === id)
    const isShotSelected = index > -1

    singleSelection
      ? isShotSelected
        ? this.setSelectedShotIds()
        : this.setSelectedShotIds([id])
      : isShotSelected
        ? this.selectedShotIds.splice(index, 1)
        : this.selectedShotIds.push(id)

    isShotSelected
      ? this.setLastSelectedShotId()
      : this.setLastSelectedShotId(id)
  }

  @action.bound updateShots (shots) {
    const shotsToUpdate = shots
      .map(shot => ({
        ...shot,
        relatedProjects: shot.relatedProjects.map(r => ({ id: r.id }))
      }))
    const {
      [EDITOR_STORE]: { videoEntityId },
      [GENERAL_STORE]: { handleApiError }
    } = this.rootStore

    Api
      .post(`/video/${videoEntityId}/saveShots`, shotsToUpdate)
      .then(this.updateShotsLocally)
      .catch(handleApiError)
  }

  @action.bound updateShot (shot, addToSelectedGroup = false) {
    const cloneShot = { ...shot }
    const {
      [EDITOR_GROUPS_STORE]: { selectedGroups }
    } = this.rootStore
    addToSelectedGroup && (cloneShot.name = selectedGroups[0])
    this.updateShots([cloneShot])
  }

  @action.bound updateShotLocally (shot) {
    const shotBeforeChange = this.getShotById(shot.id)

    const prevGroupName = shotBeforeChange.name || UNGROUPED_SHOTS
    const nextGroupName = shot.name || UNGROUPED_SHOTS
    const hasGroupChanged = prevGroupName !== nextGroupName

    const prevLocation = shotBeforeChange.location || UNLOCATED_SHOTS
    const nextLocation = shot.location || UNLOCATED_SHOTS
    const hasLocationChanged = prevLocation !== nextLocation
    this.shots[shot.id] = shot

    const {
      [EDITOR_GROUPS_STORE]: { removeShotFromGroup, addShotToGroup },
      [EDITOR_LOCATIONS_STORE]: { removeShotFromLocation, addShotToLocation }
    } = this.rootStore

    if (hasGroupChanged) {
      removeShotFromGroup(prevGroupName, shot.id)
      addShotToGroup(shot)
    }

    if (hasLocationChanged) {
      removeShotFromLocation(prevLocation, shot.id)
      addShotToLocation(shot)
    }
  }

  @action.bound updateShotsLocally (shots) {
    shots.forEach(this.updateShotLocally)
  }

  @action.bound getVideoShots (id) {
    const {
      [GENERAL_STORE]: { setLoadingOn, handleApiError },
      [EDITOR_STORE]: { videoEntityId, setVideoEntityId }
    } = this.rootStore

    !videoEntityId && setVideoEntityId(id)

    setLoadingOn()

    Api
      .get(`/video/${id}/getAllShots`)
      .then(this.handleInitialShots)
      .catch(handleApiError)
  }

  @action.bound deleteShotLocally (id) {
    const groupName = this.getShotById(id).name
    delete this.shots[id]
    groupName && this.rootStore[EDITOR_GROUPS_STORE].removeShotFromGroup(groupName, id)
  }

  @action.bound addShotLocally (shot) {
    this.shots[shot.id] = shot
    this.rootStore[EDITOR_GROUPS_STORE].addShotToGroup(shot)
  }

  @action.bound mergeShots (shot1Id, shot2Id) {
    Api
      .get(`/video/mergeShots?shot1Id=${shot1Id}&shot2Id=${shot2Id}`)
      .then(shot => {
        this.updateShotLocally(shot)
        this.deleteShotLocally(shot2Id)
      })
      .catch(this.rootStore[GENERAL_STORE].handleApiError)
  }

  @action.bound splitShot (shotId, splitFrameNumber) {
    const body = {
      id: shotId,
      splitStartFrames: [splitFrameNumber]
    }

    Api
      .post('/video/splitShots', body)
      .then(([shot, newShot]) => {
        this.updateShotLocally(shot)
        this.addShotLocally(newShot)
      })
      .catch(this.rootStore[GENERAL_STORE].handleApiError)
  }

  @action.bound clearSelectedShots () {
    this.setSelectedShotIds()
  }
}
